import React, { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  TextField
} from "@material-ui/core";

import { useStore } from "@store/store";
import Loader from "@components/loader";
import API from "@services/axios";
import {
  allowOnlyFloat,
  isSameDate,
  utcToLocal
} from "@utils/commonFunctions";
import { FuelSurchargeStyle } from "./style";
import TableListing from "./table-listing";
import FuelSurchargePopup from "./popup";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import {enAU} from "date-fns/locale";
import calendarIcon from "@assets/images/calendar-icon.svg";
import moment from "moment";
import { routes, fuelSurcharge } from "@utils/constant";
import SteppedLineChart from "./steppedLineChart";
import ConflictPopup from "./table-listing/popup/conflict-popup";

function FuelSurcharge(props) {
  const history = useHistory();
  const classes = FuelSurchargeStyle();
  const [state, dispatch] = useStore();
  const [error, setError] = useState(props.tempErrorFuelSurchargeRate.current ? props.tempErrorFuelSurchargeRate.current : "");
  const [errorDate, setErrorDate] = useState(props.tempErrorStartDate.current ? props.tempErrorStartDate.current : "");
  const { id } = useParams();
  const [startDate, setStartDate] = useState(utcToLocal(props.tempFuelSurchargeStartDate.current, state?.customer?.customerByIdData?.cities?.timezone, "YYYY-MM-DD"));
  const [data, setData] = useState(props.tempFuelSurchargeRate.current != null ? props.tempFuelSurchargeRate.current : props.fuelSurchargeRate ?? fuelSurcharge.defaultValue);
  const [isUpdateFuelSurchargeLoading, setIsUpdateFuelSurchargeLoading] = useState(false);
  const [openFuelSurChargeConflict, setOpenFuelSurChargeConflict] = useState(false);
  const [isFuelSurchargeHistoryConflictLoading, setIsFuelSurchargeHistoryConflictLoading] = useState(false);
  const [openConflictDialog, setOpenConflictDialog] = useState(false);
  const [dataFuelSurchargeConflict, setDataFuelSurchargeConflict] = useState({
    startDate: "",
    newRate: "",
    existingScheduleRate: "",
    existingScheduleStartDate: "",
    customerName: "",
    cityName: "",
  });

  const handleChange = (e) => {
    setData(e.target.value);
    props.tempFuelSurchargeRate.current = e.target.value;
    props.tempErrorFuelSurchargeRate.current = null;
  };

  const handleBlur = (e) => {
    if (e.target.value === "") {
      props.tempErrorFuelSurchargeRate.current = "Fuel surcharge is required";
    } else if (e.target.value > 1000000) {
      props.tempErrorFuelSurchargeRate.current = "Fuel surcharge must not be more than 1000000";
    } else {
      props.tempErrorFuelSurchargeRate.current = null;
    }
    setError(props.tempErrorFuelSurchargeRate.current);
  };

  // Update fuel surcharge and validation for fuel surcharge
  const handleSubmit = async () => {
    let isValid = true;
    if (data === "") {
      props.tempErrorFuelSurchargeRate.current = "Fuel surcharge is required";
    }
    else if (data > 1000000) {
      props.tempErrorFuelSurchargeRate.current = "Fuel surcharge must not be more than 1000000";
    }
    else {
      props.tempErrorFuelSurchargeRate.current = null;
    }

    if (props.tempErrorFuelSurchargeRate.current) {
      setError(props.tempErrorFuelSurchargeRate.current);
      isValid = false;
    } else {
      setError("");
    }

    if (!startDate) {
      props.tempErrorStartDate.current = "Please select a start date";
    } else if (isSameDate(startDate, props.existingActiveStartDate)) {
      props.tempErrorStartDate.current = "Day overlapped. Please update end date for the current Active rate first.";
    } else {
      props.tempErrorStartDate.current = null;
    }

    if (props.tempErrorStartDate.current) {
      setErrorDate(props.tempErrorStartDate.current);
      isValid = false;
    } else {
      setErrorDate("");
    }

    if (!isValid) return;

    if (startDate && props?.existingActiveEndDate && props?.scheduledStartDate) {
      if (new Date(startDate) > new Date() && new Date(startDate) <= new Date(props?.existingActiveEndDate)) {
        setOpenConflictDialog(true);
        return;
      }
    }

    await saveFuelSurcharge();
  };

  const saveFuelSurcharge = async () => {
    setIsUpdateFuelSurchargeLoading(true);
    const response = await API.patch(`customers/${id}/saveData`, {
      fuelSurcharge: data === null ? 0 : data,
      startDate: moment(startDate).format("YYYY-MM-DD")
    });

    setIsUpdateFuelSurchargeLoading(false);
    if (response?.data?.code === 200) {
      if (response?.data?.data?.updateCustomer) {
        toast.success("Fuel Surcharge updated successfully");
        if(response?.data?.data?.isImpactJobAndInvoice) {
          history.push(routes.backDatedEvents);
        }
        props.getCustomer();
        props.updatedFuelSurchargeRate.current = true;
      } else if (response?.data?.data?.scheduled) {
        toast.success("Fuel Surcharge scheduled successfully");
        props.getCustomer();
        props.updatedFuelSurchargeRate.current = false;
        if (!props.existingActiveStartDate) {
          props.tempFuelSurchargeRate.current = null;
        }
      } else if (response?.data?.data?.reScheduled) {
        toast.success("Fuel Surcharge re-scheduled successfully");
        props.getCustomer();
        props.updatedFuelSurchargeRate.current = false;
        if (!props.existingActiveStartDate) {
          props.tempFuelSurchargeRate.current = null;
        }
      } else if(response?.data?.data?.conflictScheduled) {
        const scheduledData = state?.customer.fuelSurchargeHistoryData?.rows?.find(
        (item) => item.status === "Scheduled");
        setDataFuelSurchargeConflict({
          startDate: new Date().toISOString().slice(0, 10),
          newRate: data === null ? 0 : data,
          existingScheduleRate: scheduledData?.fuelSurcharge,
          existingScheduleStartDate: scheduledData?.startDate,
          customerName: state.customer?.customerByIdData?.name,
          cityName: state.customer?.customerByIdData?.cities?.name,
        });
        handleOpenPopup();
      }
    } else {
      toast.error(response?.data?.message);
    }
  };

  const handleFuelSurchargeConflict = async (payload) => {
    setIsFuelSurchargeHistoryConflictLoading(true);
    const response = await API.patch(`customers/${id}/dateRangeOverlap`, {
      fuelSurcharge: dataFuelSurchargeConflict.newRate ? dataFuelSurchargeConflict.newRate : 0,
      option: payload.option
    })
    setIsFuelSurchargeHistoryConflictLoading(false);

    if (response?.data?.code === 200) {
      if (payload.option === 1) {
        // make new rate effective temporarily until the scheduled rate begins.
        toast.success(`Temporary fuel surcharge of ${dataFuelSurchargeConflict.newRate}% saved successfully. Effective from ${moment(dataFuelSurchargeConflict.startDate).format("DD-MM-YYYY")} to ${moment(dataFuelSurchargeConflict.existingScheduleStartDate).format("DD-MM-YYYY")}. The rate of ${dataFuelSurchargeConflict.existingScheduleRate}% remains scheduled for ${dataFuelSurchargeConflict.existingScheduleStartDate}.`);
      } else if (payload.option === 2) {
        // make new rate effective and permanently replace the existing scheduled rate.
        toast.success(`New fuel surcharge of ${dataFuelSurchargeConflict.newRate}% saved successfully, effective from ${moment(dataFuelSurchargeConflict.startDate).format("DD-MM-YYYY")}. The previously scheduled rate for ${moment(dataFuelSurchargeConflict.existingScheduleStartDate).format("DD-MM-YYYY")} has been cancelled.`);
      } else {
        toast.success("Fuel Surcharge Updated Successfully");
      }
      props.getCustomer();
      handleClosePopup();
    } else {
      toast.error(response?.data?.message);
    }
  }

  const handleOpenPopup = () => {
    setOpenFuelSurChargeConflict(true);
  };

  const handleClosePopup = () => {
    setOpenFuelSurChargeConflict(false);
  };

  const handleChangedDate = (date) => {
    setStartDate(date);
    props.tempFuelSurchargeStartDate.current = date;
    props.tempErrorStartDate.current = null;
    setErrorDate("");
  }

  return (
    <>
      <div className={classes.FuelSurchargeWrapper}>
        <div className="fuel-charge-holder">
          <div className="weekly-row-wrapper">
            <Loader
              loading={
                state?.customer?.loadingFuelSurchargeHistory ||
                isFuelSurchargeHistoryConflictLoading ||
                isUpdateFuelSurchargeLoading
              }
            />
            <div className="weekly-row">
              <label className="label-text">Fuel Surcharge (%) </label>
              <div className="textfield-wrapper">
                <TextField
                  id="fuel"
                  name="fuel"
                  className="fuel"
                  variant="outlined"
                  type="text"
                  placeholder="Charge"
                  onKeyPress={allowOnlyFloat}
                  value={data === null ? 0 : data}
                  onChange={handleChange}
                  error={true}
                  helperText={error}
                  onBlur={handleBlur}
                />
              </div>
            </div>
          </div>
          <div className="date-row-wrapper">
            <div className="date-row">
              <label className="label-text">Start date</label>
              <div className="textfield-wrapper">
                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={enAU}>
                  <KeyboardDatePicker
                    variant="inline"
                    format="dd/MM/yyyy"
                    placeholder="Select a date"
                    value={startDate}
                    className="custom-datepicker"
                    onChange={handleChangedDate}
                    autoOk
                    error={true}
                    helperText={errorDate}
                    keyboardIcon={<img src={calendarIcon} alt="calendar"/>}
                    TextFieldComponent={(props) => (
                      <TextField
                        {...props}
                        InputProps={{
                          ...props.InputProps,
                          readOnly: true,
                        }}
                      />)}
                  />
                </MuiPickersUtilsProvider>
              </div>
            </div>
          </div>
        </div>

        <Button
          className="blue-btn primary-btn"
          color="inherit"
          disableElevation
          underlinenone="true"
          onClick={handleSubmit}
        >
          Save
        </Button>
      </div>
      {state?.customer?.fuelSurchargeEffectiveRateData?.length > 0 && <SteppedLineChart />}
      <TableListing
        page={props.pagination.page}
        handleChangePage={props.handleChangePage}
        rowsPerPage={props.pagination.rowsPerPage}
        handleChangeRowsPerPage={props.handleChangeRowsPerPage}
        handleSorting={(e, property) => props.handleSorting(e, property)}
        orderBy={props.sort.orderBy}
        order={props.sort.order}
        data={state?.customer?.fuelSurchargeHistoryData}
        getCustomer={props.getCustomer}
        getFuelSurchargeHistory={props.getFuelSurchargeHistory}
        getFuelSurchargeEffectiveRates={props.getFuelSurchargeEffectiveRates}
        updatedFuelSurchargeRate={props.updatedFuelSurchargeRate}
        tempFuelSurchargeRate={props.tempFuelSurchargeRate}
        tempErrorFuelSurchargeRate={props.tempErrorFuelSurchargeRate}
        existingActiveStartDate={props.existingActiveStartDate}
        scheduledFuelSurchargeRate={props.scheduledFuelSurchargeRate}
        scheduledStartDate={props.scheduledStartDate}
      />
      <FuelSurchargePopup
        handleClose={handleClosePopup}
        open={openFuelSurChargeConflict}
        dataFuelSurchargeConflict={dataFuelSurchargeConflict}
        handleFuelSurchargeConflict={handleFuelSurchargeConflict}
        error={error}
      />
      <ConflictPopup
        open={openConflictDialog}
        handleClose={() => setOpenConflictDialog(false)}
        handleUpdateFuelSurcharge={saveFuelSurcharge}
        scheduledFuelSurchargeRate={props?.scheduledFuelSurchargeRate}
        scheduledStartDate={props?.scheduledStartDate}
        loading={isUpdateFuelSurchargeLoading}
      />
    </>
  );
}

export default React.memo(FuelSurcharge);
